// src/firebase/admin-auth.js
// Simplified Auth Functions - Firebase Auth handles persistence automatically
// Server-side validation via Cloud Functions ensures security

import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    signOut,
    sendPasswordResetEmail,
    sendEmailVerification
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions, googleProvider } from "./firebase";

/**
 * Google Registration
 * Signs in with Google -> Creates Store (server-side) -> Signs out
 * User must login after registration to get fresh token with proper claims
 */
export const googleRegister = async (storeData) => {
    try {
        // 1. Sign in with Google
        const userCredential = await signInWithPopup(auth, googleProvider);
        const user = userCredential.user;

        // Google automatically verifies email, so no email verification needed

        // 2. Call Server to create Store & Assign Role
        const createSecureStore = httpsCallable(functions, 'createSecureStore');
        const storeResult = await createSecureStore({
            ...storeData,
            ownerName: storeData.ownerName || user.displayName || 'Store Owner'
        });

        // 3. Force Logout - ensures token refreshes with new claims on next login
        await signOut(auth);

        return {
            success: true,
            registrationComplete: true,
            message: "Registration successful! Please sign in again with Google to access your dashboard.",
            storeId: storeResult.data.storeId,
            provider: 'google'
        };

    } catch (error) {
        console.error('Google Registration Error:', error);
        // Clean up: sign out on any failure
        if (auth.currentUser) await signOut(auth);

        // Handle specific Google errors
        if (error.code === 'auth/popup-closed-by-user') {
            throw new Error('Sign-in was cancelled. Please try again.');
        }
        if (error.code === 'auth/popup-blocked') {
            throw new Error('Pop-up blocked. Please allow pop-ups for this site and try again.');
        }
        if (error.code === 'auth/account-exists-with-different-credential') {
            throw new Error('An account with this email already exists. Try signing in with your existing method.');
        }

        throw new Error(error.message);
    }
};

/**
 * Google Login
 * Authenticates user with Google, then validates access via secure Cloud Function
 */
export const googleLogin = async () => {
    try {
        // 1. Sign in with Google
        const userCredential = await signInWithPopup(auth, googleProvider);

        // 2. Validate access server-side (ownership check)
        const getEffectiveStoreIdFunc = httpsCallable(functions, 'getEffectiveStoreId');
        const storeResult = await getEffectiveStoreIdFunc();

        if (!storeResult.data.success) {
            await signOut(auth);
            throw new Error("Authentication failed. Please register your store first or contact administrator.");
        }

        // Super admins don't have a storeId - that's okay
        if (storeResult.data.isSuperAdmin) {
            return {
                success: true,
                storeId: null,
                storeName: storeResult.data.storeName,
                isSuperAdmin: true,
                provider: 'google'
            };
        }

        // Regular admins must have a storeId
        if (!storeResult.data.storeId) {
            await signOut(auth);
            throw new Error("Store configuration not found. Please register your store first.");
        }

        // Return validated data - Context will handle state
        return {
            success: true,
            storeId: storeResult.data.storeId,
            storeName: storeResult.data.storeName,
            isSuperAdmin: false,
            provider: 'google'
        };

    } catch (error) {
        console.error('Google Login Error:', error);
        // Sign out on any failure
        if (auth.currentUser) await signOut(auth);

        // Handle specific Google errors
        if (error.code === 'auth/popup-closed-by-user') {
            throw new Error('Sign-in was cancelled. Please try again.');
        }
        if (error.code === 'auth/popup-blocked') {
            throw new Error('Pop-up blocked. Please allow pop-ups for this site and try again.');
        }
        if (error.code === 'auth/account-exists-with-different-credential') {
            throw new Error('An account with this email already exists. Try signing in with email/password instead.');
        }

        throw new Error(error.message);
    }
};

/**
 * Admin Registration
 * Creates Auth account -> Creates Store (server-side) -> Signs out
 * User must login after registration to get fresh token with proper claims
 */
export const adminRegister = async (registrationData) => {
    try {
        const { email, password, ownerName, storeName, storeDescription, storeAddress, phoneNumber } = registrationData;

        // 1. Create Authentication Account
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // 2. Send Email Verification
        await sendEmailVerification(userCredential.user);

        // 3. Call Server to create Store & Assign Role
        // Server-side function handles role assignment securely
        const createSecureStore = httpsCallable(functions, 'createSecureStore');
        const storeResult = await createSecureStore({
            storeName,
            storeDescription,
            storeAddress,
            ownerName,
            phoneNumber: phoneNumber || ""
        });

        // 4. Force Logout - ensures token refreshes with new claims on next login
        await signOut(auth);

        return {
            success: true,
            registrationComplete: true,
            message: "Registration successful! Please check your email to verify your account, then login.",
            storeId: storeResult.data.storeId
        };

    } catch (error) {
        console.error('Registration Error:', error);
        // Clean up: sign out on any failure
        if (auth.currentUser) await signOut(auth);
        throw new Error(error.message);
    }
};

/**
 * Admin Login
 * Authenticates user, then validates access via secure Cloud Function
 * Returns store data only if server-side ownership validation passes
 */
export const adminLogin = async (email, password) => {
    try {
        // 1. Authenticate with Firebase
        const userCredential = await signInWithEmailAndPassword(auth, email, password);

        // 2. Check if email is verified
        // if (!userCredential.user.emailVerified) {
        //     await signOut(auth);
        //     throw new Error("Please verify your email before logging in. Check your inbox for the verification link.");
        // }

        // 3. Validate access server-side (ownership check)
        // This Cloud Function verifies: user exists, has a store, is owner OR admin
        const getEffectiveStoreIdFunc = httpsCallable(functions, 'getEffectiveStoreId');
        const storeResult = await getEffectiveStoreIdFunc();

        if (!storeResult.data.success) {
            await signOut(auth);
            throw new Error("Authentication failed. Please contact administrator.");
        }

        // Super admins don't have a storeId - that's okay
        if (storeResult.data.isSuperAdmin) {
            return {
                success: true,
                storeId: null,
                storeName: storeResult.data.storeName,
                isSuperAdmin: true
            };
        }

        // Regular admins must have a storeId
        if (!storeResult.data.storeId) {
            await signOut(auth);
            throw new Error("Store configuration not found. Please contact administrator.");
        }

        // Return validated data - Context will handle state
        return {
            success: true,
            storeId: storeResult.data.storeId,
            storeName: storeResult.data.storeName,
            isSuperAdmin: false
        };

    } catch (error) {
        console.error('Login Error:', error);
        // Sign out on any failure
        if (auth.currentUser) await signOut(auth);
        throw new Error(error.message);
    }
};

/**
 * Admin Logout
 */
export const adminLogout = async () => {
    try {
        await signOut(auth);
        return { success: true };
    } catch (error) {
        console.error('Logout Error:', error);
        throw error;
    }
};

/**
 * Password Reset
 */
export const adminResetPassword = async (email) => {
    try {
        await sendPasswordResetEmail(auth, email, {
            url: `${window.location.origin}/admin/login`,
            handleCodeInApp: false
        });
        return {
            success: true,
            message: 'Password reset email sent successfully. Check your inbox.'
        };
    } catch (error) {
        console.error('Reset Password Error:', error);

        let errorMessage = 'Failed to send password reset email.';
        if (error.code === 'auth/user-not-found') {
            errorMessage = 'No account found with this email address.';
        } else if (error.code === 'auth/invalid-email') {
            errorMessage = 'Please enter a valid email address.';
        } else if (error.code === 'auth/too-many-requests') {
            errorMessage = 'Too many requests. Please wait before trying again.';
        }

        throw new Error(errorMessage);
    }
};